import { ROUTE_META, DEFAULT_META } from './routeMeta';

const SITE = 'https://govindaniit.com';
const ORG = `${SITE}/#organization`;

/**
 * Builds per-route JSON-LD.
 *
 * The Organization and WebSite nodes live in index.html because they are
 * identical everywhere. These are the page-specific nodes — a BreadcrumbList
 * for every page, plus a Service node on service pages so Google can tell what
 * is actually being offered rather than inferring it from prose.
 *
 * Only facts already published on the site are used. No ratings, review counts,
 * prices or availability are invented — those need a real source, and fabricated
 * AggregateRating is both a Google penalty and a lie to customers.
 */

const titleOf = (route: string) => {
  const m = ROUTE_META[route] ?? DEFAULT_META;
  return m.title.replace(/\s*\|\s*Govindani Infotech\s*$/, '').trim();
};

const descOf = (route: string) => (ROUTE_META[route] ?? DEFAULT_META).description;

const pretty = (seg: string) =>
  seg
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

function breadcrumb(route: string) {
  const segs = route.split('/').filter(Boolean);
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE }];
  let acc = '';
  segs.forEach((s, i) => {
    acc += '/' + s;
    items.push({
      '@type': 'ListItem',
      position: i + 2,
      // The leaf uses the page's real title; intermediate crumbs are derived
      // from the path, since those levels often have no page of their own.
      name: i === segs.length - 1 ? titleOf(acc) || pretty(s) : pretty(s),
      item: SITE + acc,
    });
  });
  return { '@type': 'BreadcrumbList', '@id': `${SITE}${route}#breadcrumb`, itemListElement: items };
}

/** Service pages get a Service node. Detected by path, not guessed by content. */
const SERVICE_PREFIXES = ['/services/', '/whatsapp/'];
const SERVICE_EXACT = new Set([
  '/services/lead-generation',
  '/services/ecommerce',
  '/real-estate',
  '/careers',
]);

function isServicePage(route: string) {
  if (SERVICE_EXACT.has(route)) return route !== '/careers';
  return SERVICE_PREFIXES.some((p) => route.startsWith(p));
}

function service(route: string) {
  return {
    '@type': 'Service',
    '@id': `${SITE}${route}#service`,
    name: titleOf(route),
    description: descOf(route),
    url: SITE + route,
    provider: { '@id': ORG },
    areaServed: { '@type': 'Country', name: 'India' },
    // No `offers` node: publishing a price or availability we cannot verify
    // would be worse than omitting it.
  };
}

export function schemaFor(route: string) {
  const clean = route === '/' ? '' : route.replace(/\/+$/, '');
  const graph: Record<string, unknown>[] = [];

  if (clean) graph.push(breadcrumb(clean));
  if (isServicePage(clean)) graph.push(service(clean));

  // WebPage ties the page to the site and gives it a stable identifier.
  graph.push({
    '@type': 'WebPage',
    '@id': `${SITE}${clean || '/'}#webpage`,
    url: SITE + (clean || '/'),
    name: titleOf(clean),
    description: descOf(clean),
    isPartOf: { '@id': `${SITE}/#website` },
    about: { '@id': ORG },
    inLanguage: 'en-IN',
  });

  return { '@context': 'https://schema.org', '@graph': graph };
}
